import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Container, Grid, Stack, Typography, TextField } from "@mui/material";
import MapCircle from "assets/map-circle.svg";
import MapPin from "assets/map-pin.svg";
import AccountHeader from "components/templates/AccountHeader";
import BookingService from "services/booking.service";
import { useEffect, useState } from "react";
import Date from "assets/icons/Date";
import DateService from "utils/date.util";
import { MODAL, modalActions } from "redux/slices/modal";
import Button from "components/atoms/Button";
import PartnerBackButton from "components/atoms/PartnerBackButton";
import JeenyLogo from "assets/logo.svg";
import { Phone } from "@mui/icons-material";
import CircularLoader from "components/atoms/CircleLoader";
import {
  isYeloCheckoutSession,
  withPartnerToken,
} from "utils/partnerSession.util";

export default function BookingDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const dispatch = useAppDispatch();
  const booking = useAppSelector((state) => state.booking.booking);
  const loading = useAppSelector((state) => state.booking.loading);
  console.log("🚀 ~ BookingDetails ~ loading:", loading);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!isYeloCheckoutSession() || !id || !token) return;
    if (searchParams.get("token")) return;

    navigate(withPartnerToken(`/booking/${id}`), { replace: true });
  }, [id, navigate, searchParams, token]);

  useEffect(() => {
    if (!id || !token) navigate("/not-authorize");
    BookingService.getBookingById(id, token);
  }, [token, id, navigate]);

  const handleReportProblem = () => {
    dispatch(
      modalActions.openModal({
        data: {
          bookingId: id,
          token,
          bookingNumber: booking?.booking_number
        },
        width: '80%',
        type: MODAL.REPORT_A_PROBLEM,
      })
    );
  };

  return (
    <>
      {loading ? (
        <CircularLoader />
      ) : (
        <>
          <Stack direction={"row"} alignItems={"center"} gap={1} px={2} pt={2}>
            <PartnerBackButton />
            <Box width={"100%"} margin={"auto"} textAlign={"center"}>
              <img
                src={JeenyLogo}
                alt="Jeeny"
                style={{
                  marginRight: i18n.language === "ar" ? "-46px" : "46px",
                }}
              />
            </Box>
          </Stack>
          <Container maxWidth="lg">
            <Box
              mt={2} bgcolor="#ffffff"
              borderRadius="12px" padding="12px" border="1px solid #E4E3EB"
              boxShadow="0px 8px 32px 0px rgba(42, 52, 86, 0.06)" mb={2}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>

                <Typography fontSize="14px" fontWeight={600} color="#0a0a0e" >
                  {t("needHelp") || "Need Help?"}
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReportProblem}
                  sx={{
                    borderRadius: '8px', height: '32px', textTransform: 'none',
                    fontWeight: 500, fontSize: '12px', borderColor: '#520A76', color: '#520A76',
                    '&:hover': { borderColor: '#520A76', backgroundColor: 'rgba(82, 10, 118, 0.04)' },
                    px: 2
                  }}
                >
                  {t("reportProblem") || "Report Problem"}
                </Button>
              </Stack>

            </Box>

            <Stack direction={"row"}>
              <Box width={"100%"} pt={{ xs: "16px", md: "40px" }}>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <AccountHeader
                    back={false}
                    link={`/bookings`}
                    heading={"bookingId"}
                    subHeading={booking?.booking_number}
                  />
                  {(booking?.current_status === "posted" ||
                    booking?.current_status === "accepted") && (
                      <Box
                        p="8px 12px"
                        color={"#520A76"}
                        textAlign={"center"}
                        borderRadius={"12px"}
                        border={"1px solid #520A76"}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          dispatch(
                            modalActions.openModal({
                              data: { ...booking, token },
                              width: '80%',
                              type: MODAL.UPDATE_BOOKING,
                            })
                          )
                        }
                      >
                        {t("edit")}
                      </Box>
                    )}
                </Stack>


                {booking?.current_status !== "cancelled" &&
                  booking?.driver_details?.first_name && (
                    <Box
                      p={1}
                      mt={2}
                      px={2}
                      bgcolor="#ffffff"
                      border="1px solid #DEE2E9"
                      borderRadius="16px"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      flexDirection="column"
                    >
                      <Box mb={1}>
                        <Typography
                          fontSize="12px"
                          fontWeight={300}
                        >
                          {t("The Driver for the Ride")}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" gap={1}>

                        <Box
                          width="50px"
                          height="50px"
                          p={1}
                          bgcolor="#F2F2F7"
                          borderRadius="100%"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >


                          {booking?.driver_details?.profile_pic?.url && (
                            <img
                              src={booking?.driver_details?.profile_pic?.url}
                              alt="Driver"
                              style={{
                                objectFit: "contain",
                                width: "100%",
                                height: "100%",
                                borderRadius: "100%",
                              }}
                            />
                          )}
                        </Box>

                        <Box>
                          <Typography
                            fontSize="14px"
                            textTransform={"capitalize"}
                            fontWeight={600}
                            mb={0.5}
                          >
                            {booking?.driver_details?.first_name}
                          </Typography>
                          {(booking?.current_status !== "cancelled" || booking?.current_status !== "finished") &&
                            booking?.driver_details?.phone && (
                              <Typography
                                fontSize="12px"
                                color="#939399"
                                fontWeight={300}
                                sx={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    borderRadius: '4px',
                                    padding: '2px 4px'
                                  }
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(booking?.driver_details?.phone);
                                  // Optional: Show a toast notification
                                }}
                              >
                                {booking?.driver_details?.phone}
                              </Typography>
                            )}
                          {booking?.vehicle_details?.license_plate && (
                            <Typography
                              fontSize="12px"
                              color="#939399"
                              fontWeight={300}
                            >
                              {booking?.vehicle_details?.model}
                              <br />
                              {booking?.vehicle_details?.license_plate}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {/* {(booking?.current_status !== "cancelled" || booking?.current_status !== "finished") &&
                        booking.driver_details?.phone && (
                          <Box
                            component="a"
                            href={`tel:${booking.driver_details.phone}`}
                            width="30px"
                            height="30px"
                            p={1}
                            bgcolor="#F2F2F7"
                            borderRadius="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Phone />
                          </Box>
                        )} */}
                    </Box>
                  )}

                {!booking?.driver_details?.first_name &&
                  booking?.current_status !== "cancelled" && (
                    <Box
                      mt={2}
                      px={2}
                      py={1}
                      pt={1.275}
                      bgcolor="#ffffff"
                      border="1px solid #DEE2E9"
                      borderRadius="16px"
                    >
                      <Typography fontSize="14px" fontWeight={400}>
                        {t("Driver will be assigned soon")}
                      </Typography>
                    </Box>
                  )}

                {/* {booking?.current_status === "cancelled" || !booking?.driver_details?.first_name ?
              null
              :
              <Box
                p={1}
                mt={2}
                bgcolor={"#ffffff"}
                border={"1px solid #DEE2E9"}
                borderRadius={"16px"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                px={2}
              >
                <Stack direction={"row"} alignItems={"center"} gap={1}>
                  <Box
                    width={"50px"}
                    height={"50px"}
                    padding={"8px"}
                    bgcolor={"#F2F2F7"}
                    borderRadius={"100%"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <img
                      src={booking?.driver_details?.profile_pic?.url}
                      alt=""
                      style={{ objectFit: "contain" }}
                    />
                  </Box>

                  <Box>
                    {booking?.driver_details?.first_name &&
                      <Typography
                        fontSize={"14px"}
                        mt={0}
                        mb={0.5}
                        fontWeight={600}
                      >
                        {booking?.driver_details?.first_name}
                      </Typography>
                    }
                    {!booking?.driver_details?.first_name && booking?.current_status !== "cancelled" &&
                      <Typography
                        fontSize={"14px"}
                        mt={0}
                        mb={0.5}
                        fontWeight={600}
                      >
                        Driver will be assigned soon
                      </Typography>
                    }
                    {booking?.vehicle_details?.license_plate &&
                      <Typography
                        fontSize={"12px"}
                        color={"#939399"}
                        fontWeight={300}
                      >
                        {booking?.vehicle_details?.model}
                        <br />
                        {booking?.vehicle_details?.license_plate}
                      </Typography>
                    }
                  </Box>
                </Stack>
                {booking?.current_status === "pending" && (
                  <Box
                    component={"a"}
                    href={`tel:${booking?.driver_details?.phone}`}
                    width={"30px"}
                    height={"30px"}
                    bgcolor={"#F2F2F7"}
                    borderRadius={"100%"}
                    p={1}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Phone />
                  </Box>
                )}
              </Box>
            } */}
                <Box
                  p={2}
                  mt={2}
                  bgcolor={"#ffffff"}
                  border={"1px solid #DEE2E9"}
                  borderRadius={"16px"}
                >
                  <Stack
                    gap={2}
                    marginBottom="16px"
                    paddingBottom={"8px"}
                    direction={"row"}
                    borderBottom={"1px solid #ECEBF2"}
                    justifyContent={"space-between"}
                  >
                    <Typography
                      mt={0.25}
                      fontSize={"14px"}
                      fontWeight={600}
                      color={"#0a0a0e"}
                    >
                      {booking?.class_details?.name}
                    </Typography>
                    <Stack
                      borderRadius={"16px"}
                      direction={"row"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Date width={"20px"} height={"20px"} />
                      <Typography fontSize={"12px"} mt={0.5}>
                        {DateService.getShortDateString(booking?.date)},{" "}
                        {DateService.getTimeString(booking?.date)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Stack direction={"row"} gap={1} position={"relative"}>
                        <Box
                          bgcolor={"#FFF3FA"}
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          borderRadius={"25px"}
                          width={"32px"}
                          height={"32px"}
                          minWidth={"32px"}
                          minHeight={"32px"}
                          position={"relative"}
                          zIndex={1}
                        >
                          <img src={MapCircle} alt="." />
                        </Box>
                        <Box
                          top={0}
                          bottom={"-24px"}
                          zIndex={0}
                          left={"16px"}
                          position={"absolute"}
                          borderLeft={"1px dashed #ECEBF2"}
                        ></Box>
                        <div>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {booking?.pickup?.title}
                          </Typography>
                          <Typography
                            color={"#939399"}
                            fontSize={"14px"}
                            fontWeight={300}
                          >
                            {booking?.pickup?.address}
                          </Typography>
                        </div>
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={"row"} gap={1}>
                        <Box
                          bgcolor={"#FFF3FA"}
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          borderRadius={"25px"}
                          width={"32px"}
                          height={"32px"}
                          minWidth={"32px"}
                          minHeight={"32px"}
                          position={"relative"}
                          zIndex={1}
                        >
                          <img src={MapPin} alt="!" />
                        </Box>
                        <div>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {booking?.trip_type === "hourly"
                              ? `${booking?.duration} ${t("hours")}`
                              : booking?.destination?.title}
                          </Typography>
                          <Typography
                            color={"#939399"}
                            fontSize={"14px"}
                            fontWeight={300}
                          >
                            {booking?.trip_type === "hourly"
                              ? `Incl. ${booking?.duration * 40} km`
                              : booking?.destination?.address}
                          </Typography>
                        </div>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

                <Box
                  mt={2}
                  bgcolor={"#ffffff"}
                  borderRadius={"12px"}
                  padding={"16px"}
                  border={"1px solid #E4E3EB"}
                  boxShadow={"0px 8px 32px 0px rgba(42, 52, 86, 0.06)"}
                  mb={2}
                >
                  <Typography
                    mb={1.5}
                    fontSize={"16px"}
                    fontWeight={700}
                    textTransform={"uppercase"}
                  >
                    {t("summary")}
                  </Typography>

                  <Stack gap={1}>
                    {booking?.coupon?.value > 0 ? (
                      <>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          gap={1}
                        >
                          <Typography fontSize={"14px"} fontWeight={300}>
                            {t("subtotalInclVat")}
                          </Typography>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {(((booking?.price || 0) + (booking?.coupon?.value || 0))).toFixed?.(2)}{" "}
                            {booking?.price_details.currency_sign}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          gap={1}
                        >
                          <Typography fontSize={"14px"} fontWeight={300}>
                            {t("BooingDiscount")} 
                          </Typography>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            -{booking?.coupon?.value?.toFixed?.(2)}{" "}
                            {booking?.price_details.currency_sign}
                          </Typography>
                        </Stack>
                        {/* <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          gap={1}
                        >
                          <Typography fontSize={"14px"} fontWeight={300}>
                            {t("netPrices")}
                          </Typography>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {((booking?.price || 0) / (1 + booking?.price_details.tax / 100)).toFixed?.(2)}{" "}
                            {booking?.price_details.currency_sign}
                          </Typography>
                        </Stack>
                        <Stack
                          direction={"row"}
                          justifyContent={"space-between"}
                          gap={1}
                        >
                          <Typography fontSize={"14px"} fontWeight={300}>
                            {t("bookingVat")} {booking?.price_details.tax}%
                          </Typography>
                          <Typography fontSize={"14px"} fontWeight={600}>
                            {((booking?.price || 0) - ((booking?.price || 0) / (1 + booking?.price_details.tax / 100))).toFixed?.(2)}{" "}
                            {booking?.price_details.currency_sign}
                          </Typography>
                        </Stack> */}
                      </>
                    ) : (
                      <>
                        {booking?.price_details.netto_price > 0 && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                            gap={1}
                          >
                            <Typography fontSize={"14px"} fontWeight={300}>
                              {t("netPrices")}
                            </Typography>
                            <Typography fontSize={"14px"} fontWeight={600}>
                              {booking?.price_details.netto_price.toFixed?.(2)}{" "}
                              {booking?.price_details.currency_sign}
                            </Typography>
                          </Stack>
                        )}
                        {booking?.price_details.VAT > 0 && (
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                            gap={1}
                          >
                            <Typography fontSize={"14px"} fontWeight={300}>
                              {t("bookingVat")} {booking?.price_details.tax}%
                            </Typography>
                            <Typography fontSize={"14px"} fontWeight={600}>
                              {booking?.price_details.VAT.toFixed?.(2)}{" "}
                              {booking?.price_details.currency_sign}
                            </Typography>
                          </Stack>
                        )}
                      </>
                    )}
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      gap={1}
                      pt={1}
                      mt={1}
                      borderTop={"1px solid #ECEBF2"}
                    >
                      <Typography
                        fontSize={"14px"}
                        fontWeight={300}
                        color={"#520A76"}
                      >
                        {t("total")} {t("price")}
                      </Typography>
                      <Typography
                        fontSize={{ xs: "18px", md: "24px" }}
                        fontWeight={700}
                        color={"#520A76"}
                      >
                        {booking?.price.toFixed?.(2)}{" "}
                        {booking?.price_details.currency_sign}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                {(booking?.current_status === "posted" ||
                  booking?.current_status === "accepted" ||
                  booking?.current_status === "ongoing" ||
                  booking?.current_status === "arrived") && (
                    <Button
                      fullWidth
                      color="error"
                      variant="outlined"
                      sx={{
                        height: "50px",
                        borderRadius: "12px",
                        marginTop: "16px",
                      }}
                      onClick={() =>
                        dispatch(
                          modalActions.openModal({
                            width: '80%',
                            type: MODAL.CONFIRMATION_FORM,
                            data: {
                              id: booking._id,
                              type: MODAL.CANCEL_BOOKING,
                              heading: t("cancelBooking"),
                              message: t("confirmCancel"),
                              token,
                            },
                          })
                        )
                      }
                    >
                      {t("cancelBooking")}
                    </Button>
                  )}
              </Box>
            </Stack>
          </Container>
        </>
      )}

      {/* <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") setOpen(false);
        }}
      >
        <Box
          sx={{
            width: "80%",
            top: "50%",
            left: "50%",
            position: "absolute",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "none",
            boxShadow: 24,
            p: 4,
            maxWidth: "500px",
          }}
        >
          <Close
            onClick={() => setOpen(false)}
            style={{
              position: "absolute",
              top: "15px",
              right: i18n.language === "ar" ? "auto" : "16px",
              left: i18n.language === "ar" ? "16px" : "auto",
              cursor: "pointer",
            }}
          />
          <ReportAProblem setOpen={setOpen} />
        </Box>
      </Modal> */}
    </>
  );
}
